import type { BiocideFamilyApiResponse, NutritiveElement } from '@b2ag/module-product-sdk'
import type { PRODUCT_TYPES } from '../domain/product'
import type { VegetativeStageByCrops } from './PProductDetailBlockCrops/typings'

type ID = string

type ProductCore = {
  _id: ID
  slug: string
  name: string
  description?: string
  characteristics?: string
  cooperativeReview?: {
    tips?: string
    upsides?: string
  }
  displayType?: DisplayType
  variants: Array<ProductVariant>
  supplier?: string
  kind: string
  isCooperativeProduct?: boolean
  productSheetUrls?: Array<string>
  productManualUrls?: Array<string>
  family?: string
  categories?: Array<Array<{ label: string }>>
  main_category?: Array<{ label: string }>
  provider?: string
  organic?: boolean
  chlortoluron?: boolean
  resistance?: string
  generation?: string
  alternativity?: string
  profile?: string
  mixComponents?: string[]
  typeEng?: string
  useEndDatePm?: string
  marketingEndDatePm?: string
  biocide_use?: Array<{ code: string; label: string }>
  valuation?: string[]
}

export enum DisplayType {
  GENERIC = 'GEN',
  PHYTO = 'PHY',
  SEMENCE = 'SEM',
  ENGRAIS = 'ENG',
  AME = 'AME',
  SERVICE = 'SVC',
  EQE = 'EQE',
  EQC = 'EQC',
}

export type SingleProduct = ProductCore & {
  type: PRODUCT_TYPES.SINGLE_PRODUCT
  active_substances?: Array<ActiveSubstance>
  formulation?: string
  market_authorization?: string
  safetySheetUrls?: Array<string>
  toxicities?: Array<Toxicity>
  biocideFamily?: Array<BiocideFamilyApiResponse>
  usages_by_crops?: Array<UsageByCrop>
  isRestrictedExplosivesPrecursor?: boolean
  nutritive_elements?: Array<NutritiveElement>
  nutritiveElementsUnit?: string
  culturesWithVegetativeStage?: Array<VegetativeStageByCrops>
  mixed?: string
  origin?: string
  contributionMode?: string
  standard?: string
  stimulatingSubstance?: string
  shape?: string
}

export type Pack = ProductCore & {
  type: PRODUCT_TYPES.PACK
  active_substances?: Array<FlattenedAttribute<Array<ActiveSubstance>>>
  formulation?: Array<FlattenedAttribute<string>>
  market_authorization?: Array<FlattenedAttribute<string>>
  safetySheetUrls?: Array<FlattenedAttribute<Array<string>>>
  toxicities?: Array<FlattenedAttribute<Array<Toxicity>>>
  usages_by_crops?: Array<FlattenedAttribute<Array<UsageByCrop>>>
}

export type Product = Pack | SingleProduct

export function isFlattened<T>(item: T | FlattenedAttribute<T>): item is FlattenedAttribute<T> {
  return typeof item === 'object' && item !== null && 'product_name' in item
}

export function isFlattenedArray<T>(items: T | Array<FlattenedAttribute<T>>): items is Array<FlattenedAttribute<T>> {
  const item0 = items?.[0]
  return !!item0 && isFlattened(item0)
}

type FlattenedAttribute<T> = {
  product_name: string
  value: T
}

export type TechnicalSale = {
  first_name: string
  last_name: string
  mobile_phone: string
  email: string
  expertise_domains?: string
  role?: string
}

type UsageByCrop = {
  crop_id: string
  crop_name: string
}

type ProductVariant = {
  id: string
  name: string
  offline?: boolean
  sellable?: boolean
  isSoldAsUnit: boolean
  measure_quantity: number
  offers: Array<{
    allowedWithoutStock: boolean
  }>
  precocity?: string
  stockManagementType?: 'managed' | 'notManaged'
}

export type ActiveSubstance = {
  name: string
  value: string
  unit: string
}

type Toxicity = {
  id: string
  name: string
}
